import React, { useState } from "react";
import { decodeHtmlEntities } from "../../../utils/htmlUtils";
import ArticleActionsMenu from "../ArticleActionsMenu";
import { formatDate } from "../../../utils/date.utils";
import { GrantsLabelItem, ArticleLabelMap } from "../../../services/grantsApi";
import LabelCell from "../labels/LabelCell";
import { getColorConfig } from "../labels/labelColors";

interface BurArticleItem {
  id: number;
  title: string;
  url: string;
  scrapedAt: string;
  publishedAt: string | null;
  applicationDateFrom: string | null;
  applicationDateTo: string | null;
  isContinuous: boolean;
  isAloc: boolean;
  operator: {
    id: number;
    name: string;
    voivodeship: string;
    projectName: string | null;
    baseUrl: string;
    target: string | null;
  };
}

interface SortConfig {
  column:
    | "scrapedAt"
    | "publishedAt"
    | "applicationDateFrom"
    | "applicationDateTo";
  order: "ASC" | "DESC";
}

interface BurArticlesTableProps {
  articles: BurArticleItem[];
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSort?: SortConfig | null;
  onVoivodeshipClick?: (voivodeship: string) => void;
  userRole?: string;
  onReprocess?: (
    articleId: string | number,
    type: "kfs" | "bur"
  ) => Promise<void>;
  onReport?: (articleId: string | number, type: "kfs" | "bur") => void;
  onClose?: (articleId: string | number, type: "kfs" | "bur") => Promise<void>;
  onEditFlags?: (articleId: string | number, type: "kfs" | "bur") => void;
  showDebugInfo?: boolean;
  labels?: GrantsLabelItem[];
  articleLabels?: ArticleLabelMap;
  onSetArticleLabel?: (articleId: string, labelId: string | null) => Promise<any>;
}

const BurArticlesTable: React.FC<BurArticlesTableProps> = ({
  articles,
  onSortChange,
  currentSort,
  onVoivodeshipClick,
  userRole,
  onReprocess,
  onReport,
  onClose,
  onEditFlags,
  showDebugInfo,
  labels = [],
  articleLabels = {},
  onSetArticleLabel,
}) => {
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(`Skopiowano ${label}: ${text}`);
      setTimeout(() => setCopiedText(null), 2500);
    } catch (err) {
      // Fallback dla starszych przeglądarek
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedText(`Skopiowano ${label}: ${text}`);
      setTimeout(() => setCopiedText(null), 2500);
    }
  };
  const handleSort = (column: string) => {
    if (!onSortChange) return;

    let order = "DESC";
    if (currentSort?.column === column && currentSort.order === "DESC") {
      order = "ASC";
    }

    onSortChange(column, order);
  };

  const getSortIcon = (column: string) => {
    if (currentSort?.column !== column) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-neutral-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    }

    return currentSort.order === "ASC" ? (
      <svg
        className="w-4 h-4 ml-1 text-primary-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1 text-primary-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  const renderTargetBadge = (article: BurArticleItem) => {
    if (!article.operator.target) return null;

    const targetConfig = {
      FIRMY: {
        label: "Pracodawcy",
        className: "bg-blue-100 text-blue-800",
      },
      DOROSLI: {
        label: "Osoby dorosłe",
        className: "bg-purple-100 text-purple-800",
      },
      DOROSLI_TRANSFORMACJA: {
        label: "Zielona transformacja",
        className: "bg-emerald-100 text-emerald-800",
      },
    };

    const config =
      targetConfig[article.operator.target as keyof typeof targetConfig];
    if (!config) return null;

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mr-1 mb-1 ${config.className}`}
        title={`Target: ${config.label}`}
      >
        {config.label}
      </span>
    );
  };

  return (
    <div className="overflow-x-auto relative">
      {/* Toast notification */}
      {copiedText && (
        <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 max-w-sm">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm font-medium">{copiedText}</span>
          </div>
        </div>
      )}

      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th
              className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 w-24"
              onClick={() => handleSort("publishedAt")}
            >
              <div className="flex items-center">
                Publ.
                {getSortIcon("publishedAt")}
              </div>
            </th>
            <th
              className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 w-24"
              onClick={() => handleSort("applicationDateFrom")}
            >
              <div className="flex items-center">
                Od
                {getSortIcon("applicationDateFrom")}
              </div>
            </th>
            <th
              className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 w-24"
              onClick={() => handleSort("applicationDateTo")}
            >
              <div className="flex items-center">
                Do
                {getSortIcon("applicationDateTo")}
              </div>
            </th>
            <th className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-20">
              Województwo
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/3">
              Projekt
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/3">
              Tytuł
            </th>
            {labels.length > 0 && (
              <th className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-20 md:w-28">
                Etykieta
              </th>
            )}
            {(userRole === "admin" || showDebugInfo) && (
              <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                ID
              </th>
            )}
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-16">
              Akcje
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-200">
          {articles.map((article) => {
            const artId = article.id.toString();
            const artLabel = articleLabels[artId];
            const rowColor = artLabel ? getColorConfig(artLabel.color) : null;
            return (
            <tr
              key={article.id}
              className={rowColor ? `${rowColor.rowBg}` : "hover:bg-neutral-50"}
            >
              <td className="px-2 py-2 whitespace-nowrap text-sm text-left text-neutral-500">
                {formatDate(article.publishedAt)}
              </td>
              <td className="px-2 py-2 whitespace-nowrap text-sm text-left text-neutral-500">
                {formatDate(article.applicationDateFrom)}
              </td>
              <td className="px-2 py-2 whitespace-nowrap text-sm text-left text-neutral-500">
                {formatDate(article.applicationDateTo)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-left">
                <button
                  onClick={() =>
                    onVoivodeshipClick?.(article.operator.voivodeship)
                  }
                  className="text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                  title={`Filtruj według województwa: ${article.operator.voivodeship}`}
                >
                  {article.operator.voivodeship}
                </button>
              </td>
              <td className="px-3 py-2 text-sm text-left max-w-xs">
                <div className="flex flex-col">
                  <a
                    href={article.operator.baseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800 hover:underline font-medium truncate"
                    title={
                      article.operator.projectName || article.operator.name
                    }
                  >
                    {article.operator.projectName || article.operator.name}
                  </a>
                  <span
                    className="text-xs text-neutral-500 truncate"
                    title={article.operator.name}
                  >
                    {article.operator.name}
                  </span>
                </div>
              </td>
              <td className="px-3 py-2 text-sm text-left text-neutral-900">
                {renderTargetBadge(article)}
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-800 hover:underline font-medium max-w-md truncate block"
                  title={decodeHtmlEntities(article.title)}
                >
                  {(() => {
                    const decodedTitle = decodeHtmlEntities(article.title);
                    return decodedTitle.length > 80
                      ? `${decodedTitle.substring(0, 80)}...`
                      : decodedTitle;
                  })()}
                </a>
              </td>
              {labels.length > 0 && (
                <td className="px-2 py-2 whitespace-nowrap text-sm">
                  {onSetArticleLabel && (
                    <LabelCell
                      articleId={artId}
                      labels={labels}
                      currentLabel={artLabel || null}
                      onSetLabel={onSetArticleLabel}
                    />
                  )}
                </td>
              )}
              {(userRole === "admin" || showDebugInfo) && (
                <td className="px-3 py-2 whitespace-nowrap text-sm text-left text-neutral-700">
                  <button
                    onClick={() => copyToClipboard(article.id.toString(), "ID")}
                    className="font-mono text-xs hover:bg-neutral-100 hover:text-neutral-900 cursor-pointer transition-colors px-1 py-1 rounded"
                    title={`Kliknij aby skopiować ID: ${article.id}`}
                  >
                    {article.id}
                  </button>
                </td>
              )}
              <td className="px-3 py-2 whitespace-nowrap text-sm">
                {onReprocess && onReport && (
                  <ArticleActionsMenu
                    articleId={article.id}
                    articleType="bur"
                    articleTitle={article.title}
                    articleUrl={article.url}
                    isAdmin={userRole === "admin"}
                    onReprocess={onReprocess}
                    onReport={onReport}
                    onClose={onClose}
                    onEditFlags={onEditFlags}
                  />
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default BurArticlesTable;
